import { FileSpreadsheet, ListChecks, Wallet } from 'lucide-react';

import { humanizeLabel } from '@/nucleo/displayLabels';
import { formatarDinheiro } from '@/nucleo/dinheiro';
import { EmptyState } from '@/ui/EmptyState';
import { Spinner } from '@/ui/Spinner';
import { StatusBadge, type StatusVariant } from '@/ui/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Progress } from '@/ui/base/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';

import {
  useTermosDoCredenciado,
  usePerfilFinanceiroDoCredenciado,
  type MedicaoDoCredenciado,
} from './detalheDados';

interface AbaVisaoFinanceiraProps {
  credenciadoId: string;
}

const SELO_DA_MEDICAO: Record<MedicaoDoCredenciado['status'], StatusVariant> = {
  RASCUNHO: 'rascunho',
  PROCESSADA: 'processado',
  APROVADA: 'aprovado',
  EXPORTADA_SOF: 'processado',
  EMPENHADA: 'ativo',
  LIQUIDADA: 'ativo',
  AUTORIZADA: 'ativo',
  PAGA: 'pago',
};

/**
 * Aba "Visão Financeira" — versão condensada do `PerfilFinanceiroTab` do
 * protótipo (270 linhas + hook de agregação de ~140 linhas). Lá os totais eram
 * SOMADOS em tempo real a partir de termos, empenhos e medições reais; aqui,
 * sem essas tabelas na API, o resumo é o próprio dado mockado — pré-computado,
 * não recalculado (CLAUDE.md, "Proibido", exceção de 2026-08-27).
 */
export function AbaVisaoFinanceira({ credenciadoId }: AbaVisaoFinanceiraProps) {
  const { data: perfil, isLoading } = usePerfilFinanceiroDoCredenciado(credenciadoId);
  const { data: termos } = useTermosDoCredenciado(credenciadoId);

  if (isLoading || !perfil) {
    return <Spinner size="sm" />;
  }

  const hasReserva = perfil.reserva_sof > 0;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          icon={Wallet}
          rotulo="Reserva SOF"
          valor={perfil.reserva_sof}
          dica={`${perfil.qtd_termos_ativos} termo(s) ativo(s)`}
        />
        <Kpi
          icon={FileSpreadsheet}
          rotulo="Empenhado"
          valor={perfil.empenhado}
          dica={
            perfil.percentual_consumido != null
              ? `${perfil.percentual_consumido.toFixed(1)}% da reserva`
              : 'sem reserva declarada'
          }
        />
        <Kpi rotulo="Liquidado" valor={perfil.liquidado} />
        <Kpi rotulo="Autorizado" valor={perfil.autorizado} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi rotulo="Pago" valor={perfil.pago} />
        <Kpi
          rotulo="Saldo disponível"
          valor={perfil.saldo_disponivel ?? 0}
          dica={perfil.saldo_disponivel == null ? 'sem reserva declarada' : undefined}
        />
      </div>

      {hasReserva && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Consumo da reserva SOF</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Progress value={Math.min(100, perfil.percentual_consumido ?? 0)} />
            <div className="text-muted-foreground flex justify-between text-xs">
              <span>
                Empenhado:{' '}
                <span className="text-foreground font-medium">
                  {formatarDinheiro(perfil.empenhado)}
                </span>
              </span>
              <span>
                Reserva:{' '}
                <span className="text-foreground font-medium">
                  {formatarDinheiro(perfil.reserva_sof)}
                </span>
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex-row items-center gap-2 space-y-0 pb-3">
          <ListChecks className="text-muted-foreground h-4 w-4" aria-hidden="true" />
          <CardTitle className="text-sm">Termos do credenciado</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!termos || termos.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title="Sem termos vinculados"
                description="Este credenciado ainda não possui termos."
              />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Termo</TableHead>
                  <TableHead>SEI</TableHead>
                  <TableHead className="text-right">Valor mensal</TableHead>
                  <TableHead className="text-right">Reserva SOF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {termos.map((termo) => (
                  <TableRow key={termo.id}>
                    <TableCell className="font-medium">{termo.numero_termo ?? termo.id}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {termo.numero_sei ?? '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatarDinheiro(termo.valor_mensal)}
                    </TableCell>
                    <TableCell className="text-right">
                      {termo.saldo_referencia != null
                        ? formatarDinheiro(termo.saldo_referencia)
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Últimas medições</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {perfil.medicoes.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title="Sem medições"
                description="Ainda não há medições geradas para este credenciado."
              />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Competência</TableHead>
                  <TableHead>Termo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {perfil.medicoes.map((medicao) => (
                  <TableRow key={medicao.id}>
                    <TableCell className="font-medium">{medicao.mes_referencia}</TableCell>
                    <TableCell className="text-sm">{medicao.numero_termo ?? '—'}</TableCell>
                    <TableCell>
                      <StatusBadge
                        status={SELO_DA_MEDICAO[medicao.status]}
                        label={humanizeLabel(medicao.status)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {formatarDinheiro(medicao.valor_total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({
  icon: Icon,
  rotulo,
  valor,
  dica,
}: {
  icon?: typeof Wallet;
  rotulo: string;
  valor: number;
  dica?: string;
}) {
  return (
    <Card>
      <CardContent className="space-y-1 p-4">
        <div className="text-muted-foreground flex items-center gap-2 text-xs">
          {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
          <span>{rotulo}</span>
        </div>
        <div className="text-lg font-semibold">{formatarDinheiro(valor)}</div>
        {dica && <div className="text-muted-foreground text-[11px]">{dica}</div>}
      </CardContent>
    </Card>
  );
}
